import { useEffect, useState } from 'react';
import { EV, type SessionSnapshot } from '@numslearn/shared';
import { useSettings } from '../settings.js';
import { ask, getSocket } from '../net/socket.js';
import { navigate } from '../router.js';

/**
 * The projector.
 *
 * Read from the back of a classroom: the join code is the largest thing on the
 * screen until play starts, then the leaderboard takes over. Controls sit in
 * one row so the teacher's hand knows where to go without looking.
 */
export function Host({ code }: { code: string }) {
  const { t, num } = useSettings();
  const [snapshot, setSnapshot] = useState<SessionSnapshot | null>(null);
  const [attached, setAttached] = useState<boolean | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const s = getSocket();
    const attach = () => {
      void ask<{ ok: boolean; snapshot?: SessionSnapshot }>(EV.hostAttach, { code })
        .then((r) => {
          setAttached(r.ok);
          if (r.snapshot) setSnapshot(r.snapshot);
        })
        .catch(() => setAttached(false));
    };
    const onState = (snap: SessionSnapshot) => setSnapshot(snap);
    s.on('connect', attach);
    s.on(EV.state, onState);
    if (s.connected) attach();
    return () => { s.off('connect', attach); s.off(EV.state, onState); };
  }, [code]);

  if (attached === false) {
    return (
      <div className="host-empty">
        <p>{t('join.err.no_session')}</p>
        <button className="gbtn pri" onClick={() => navigate('/teacher')}>{t('home.teacherGo')}</button>
      </div>
    );
  }

  const phase = snapshot?.phase ?? 'lobby';
  const joinUrl = `${window.location.host}/join/${code}`;
  const send = (event: string, payload?: unknown) => { void ask(event, payload ?? {}).catch(() => undefined); };

  return (
    <div className="host">
      <header className="host-hd">
        <div>
          <span className="host-k">{t('host.joinAt')}</span>
          <strong className="host-url">{joinUrl}</strong>
        </div>
        <div className="host-code-box">
          <span className="host-k">{t('host.code')}</span>
          <strong className="host-code">{code}</strong>
          {snapshot?.teacherName && (
            <span className="host-who">{t('class.of', { name: snapshot.teacherName })}</span>
          )}
        </div>
        <button
          className="gbtn"
          onClick={() => {
            void navigator.clipboard?.writeText(`${window.location.origin}/join/${code}`);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1800);
          }}
        >
          {copied ? t('teacher.copied') : t('teacher.copy')}
        </button>
      </header>

      <div className="host-main">
        <section className="host-board">
          <div className="host-phase">
            <span className="host-phase-k">
              {snapshot?.round
                ? t('live.round', { a: snapshot.round.index, b: snapshot.round.total })
                : t('host.lobby')}
            </span>
            {snapshot?.secondsLeft !== null && snapshot?.secondsLeft !== undefined && (
              <span className="host-clock" data-warn={snapshot.secondsLeft <= 10}>{num(snapshot.secondsLeft)}</span>
            )}
          </div>

          {(!snapshot || snapshot.players.length === 0) ? (
            <p className="host-none">{t('host.noPlayers')}</p>
          ) : (
            <ol className="host-list">
              {snapshot.players.map((p) => (
                <li key={p.id} className={p.connected ? '' : 'is-off'} data-rank={p.rank}>
                  <span className="host-rank">{num(p.rank)}</span>
                  <span className="host-name">{p.nickname}</span>
                  <span className="host-progress" aria-hidden="true">
                    <i style={{ width: `${Math.round(p.progress * 100)}%` }} data-done={p.cleared} />
                  </span>
                  <span className="host-score">{num(p.score)}</span>
                  <button className="host-kick" onClick={() => send(EV.hostKick, { playerId: p.id })}>
                    {t('host.kick')}
                  </button>
                </li>
              ))}
            </ol>
          )}
        </section>

        <aside className="host-side">
          <span className="host-k">{snapshot?.className}</span>
          <p className="host-mode">{t(snapshot?.mode === 'race' ? 'teacher.race' : 'teacher.lockstep')}</p>
          <p className="host-count">{t('live.players', { n: snapshot?.players.length ?? 0 })}</p>
        </aside>
      </div>

      <footer className="host-controls">
        {(phase === 'lobby') && (
          <button className="gbtn pri lg" onClick={() => send(EV.hostStart)}>{t('host.start')}</button>
        )}
        {phase === 'round_active' && (
          <>
            <button className="gbtn lg" onClick={() => send(EV.hostPause)}>{t('host.pause')}</button>
            <button className="gbtn lg" onClick={() => send(EV.hostSkip)}>{t('host.skip')}</button>
          </>
        )}
        {phase === 'round_paused' && (
          <button className="gbtn pri lg" onClick={() => send(EV.hostResume)}>{t('host.resume')}</button>
        )}
        {phase === 'round_results' && (
          <button className="gbtn pri lg" onClick={() => send(EV.hostNext)}>{t('host.next')}</button>
        )}
        {phase !== 'ended' && (
          <button
            className="gbtn lg danger"
            onClick={() => { if (window.confirm(t('host.endAsk'))) send(EV.hostEnd); }}
          >
            {t('host.end')}
          </button>
        )}
        {phase === 'ended' && (
          <button className="gbtn pri lg" onClick={() => navigate('/teacher')}>{t('home.teacherGo')}</button>
        )}
      </footer>
    </div>
  );
}
